/**
 * Decides whether blurring a field in the lead-info edit form should raise the
 * "Save changes?" confirmation.
 *
 * The prompt exists to catch an agent who wanders off with unsaved edits. It
 * must NOT fire when focus lands on the panel's own "Update Lead Info" button.
 *
 * That button lives outside `<form ref="myForm">`, and the check used to be
 * `myForm.contains(relatedTarget)`. Browsers move focus on mousedown, so
 * clicking Update blurred the field first, the button read as "outside the
 * form", and the confirm dialog opened *before* the click resolved. The
 * mouseup then landed on the SweetAlert backdrop instead of the button, so the
 * click never reached `submit()` — and dismissing the dialog restored
 * `originalFormData`. Net effect: clicking Update silently threw the edits
 * away.
 *
 * `panelEl` is therefore the whole edit panel (Update button *and* the form),
 * not just the form.
 *
 * @param {object}  args
 * @param {?Element} args.relatedTarget  element receiving focus (blur event's relatedTarget)
 * @param {?Element} args.panelEl        the edit panel root
 * @param {boolean}  args.hasChanges     whether formData diverges from originalFormData
 * @returns {boolean}
 */
export function shouldPromptSaveChanges({ relatedTarget = null, panelEl = null, hasChanges = false } = {}) {
    if (! hasChanges) {
        return false;
    }

    // Focus went nowhere focusable (or we have no panel to measure against) —
    // the agent is still on the page, so stay quiet.
    if (! relatedTarget || ! panelEl) {
        return false;
    }

    return ! panelEl.contains(relatedTarget);
}
